import { isPlatformBrowser } from '@angular/common';
import { ChangeDetectorRef, Component, EventEmitter, HostListener, Inject, Input, OnChanges, Output, PLATFORM_ID, SimpleChanges } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import Compressor from 'compressorjs';
import { WebcamImage } from 'ngx-webcam';
import { PDFDocument } from 'pdf-lib';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { UploadComponentString } from 'src/app/shared/content/content';
import Swal from 'sweetalert2';
import { FilesService } from '../../services/files.service';
import { UserAgentService } from '../../services/user-agent.service';

@Component({
    selector: 'app-single-uploader',
    templateUrl: './single-uploader.component.html',
    styleUrls: ['./single-uploader.component.scss', '../modal/modal.component.scss'],
    standalone: false
})
export class SingleUploaderComponent implements OnChanges {
  // Outputs
  @Output() fileSelected = new EventEmitter<File | null>();
  @Output() successUpload = new EventEmitter<boolean>();
  @Output() selectedFile = new EventEmitter<any>();
  @Output() fileDeleted = new EventEmitter<{ fileName: string; blobFileId: number | null }>();
	@Output() fileUploaded = new EventEmitter<{ fileName: string; uri: string; type: number; documentId?: number }>();

  // Inputs - compatibilidad con upload component
  @Input() public documentType: any;
  @Input() public checklist: boolean = false;
  @Input() public responsive: boolean = false;
  @Input() public fileName: any;
  @Input() public isPendence: boolean = false;
  @Input() public preview: boolean = false;
  @Input() public userId?: string;
  @Input() public clientType: string = 'PF';
  @Input() public acceptAllFiles: boolean = false;
  @Input() public blobFiles: any[] = [];
	@Input() public readonly: boolean = false;

  // Estado del componente
  public isMobile: boolean = false;
  public isPending = false;
  public fieldId = Math.floor(Math.random() * Date.now());
  public showSnapshotModal = false;
  public success: boolean | undefined;
  public error = '';
  public webcamWidth = 600;
  public webcamHeight = 500;

  // Estado drag & drop
  public isDragging = false;
  public errorMessage: string | null = null;

  // Vista previa
  public previewUrl: string | null = null;
  public sanitizedPreviewUrl: SafeResourceUrl | null = null;
  public uploadedFile: File | null = null;
  public previewLoading: boolean = false;
  public previewProgress: number | null = null;

  // Eliminación
  public deleting: boolean = false;

  // Modal de vista previa
  public showPreviewModal = false;

  // Webcam
  private trigger: Subject<void> = new Subject<void>();
  private webcamImage!: WebcamImage;
  private capturedImageBase64 = '';

  // Textos
  uploadComponentString = UploadComponentString;

  // Configuración de validación
  private readonly allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
  private readonly allowedExtensions = ['.pdf', '.jpeg', '.jpg', '.png'];
  private readonly maxSizeInBytes = 60 * 1024 * 1024; // 60MB

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private filesService: FilesService,
    private userAgent: UserAgentService,
    private ref: ChangeDetectorRef,
    private sanitizer: DomSanitizer
  ) {
    this.isMobile = userAgent.verifyMobile();
  }

  ngOnInit(): void {
    this.convertUploadLabelName();
    this.waitForUrlAndLoad();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Solo verificar si cambió blobFiles
    if (changes['blobFiles']) {
      // Para documentos pendientes, siempre verificar el estado actual
      // Para otros documentos, solo cargar si no tiene fileName
      if (this.isPendence || !this.fileName) {
        this.checkForExistingFile();
      }
    }
  }

  private convertUploadLabelName(): void {
    if (this.documentType?.label === 'CNH') this.documentType.label = 'Enviar foto da CNH';
    if (this.documentType?.label === 'Selfie') this.documentType.label = 'Enviar selfie';
    if (this.documentType?.label === 'Requerimento empresário') this.documentType.label = 'Contrato Social+ Última  alteração contratual';
  }

  private waitForUrlAndLoad(): void {
    let attempts = 0;
    const maxAttempts = 20; // Máximo 2 segundos (20 * 100ms)

    const checkInterval = setInterval(() => {
      attempts++;

      if (this.blobFiles && this.blobFiles.length > 0) {
        clearInterval(checkInterval);
        this.checkForExistingFile();
      } else if (attempts >= maxAttempts) {
        clearInterval(checkInterval);
      }
    }, 100);
  }

  private checkForExistingFile(): void {
    // Caso 1: No hay blobFiles
    if (!this.blobFiles || this.blobFiles.length === 0) {
      // Solo limpiar si es documento pendiente o si teníamos un fileName
      if (this.isPendence && this.fileName) {
        this.clear();
        this.detectChanges();
      }
      return;
    }

    // Caso 2: Buscar el archivo en blobFiles que coincida con el type de este componente
   const blob = this.blobFiles.find((file: any) =>
			file.type === this.documentType?.type &&
			(this.documentType?.documentId == null || file.pendencyId === this.documentType?.documentId)
		);

    // Caso 3: Se encontró el archivo en blobFiles
    if (blob?.url) {
      const fileName = this.extractFileNameFromUrl(blob.url);

      if (this.fileName !== fileName) {
        this.fileName = fileName;
        this.success = true;
        this.detectChanges();
      }
    }
    // Caso 4: NO se encontró el archivo en blobFiles
    else {
      // Para documentos pendientes, SIEMPRE limpiar si no se encuentra
      // Para otros documentos, solo limpiar si ya teníamos un fileName
      if (this.isPendence || this.fileName) {
        this.clear();
        this.detectChanges();
      }
    }
  }

  private extractFileNameFromUrl(url: string): string {
    try {
      // Buscar la posición después de 'ondemand-v3/'
      const containerPath = 'ondemand-v3/';
      const containerIndex = url.indexOf(containerPath);

      if (containerIndex !== -1) {
        // Extraer todo después de 'ondemand-v3/'
        const fileName = url.substring(containerIndex + containerPath.length);
        return fileName || 'archivo-existente';
      }

      // Fallback: extraer solo el último segmento de la URL
      const urlParts = url.split('/');
      return urlParts[urlParts.length - 1] || 'archivo-existente';
    } catch (error) {
      return 'archivo-existente';
    }
  }

  private fetchPreviewFromBackend(openModal: boolean): void {
    try {
      const fileName = this.fileName || this.extractFileNameFromUrl(
				this.blobFiles?.find((f:any) =>
					f.type === this.documentType?.type &&
					(this.documentType?.documentId == null || f.pendencyId === this.documentType?.documentId)
				)?.url || ''
			);
      if (!fileName) {
        return;
      }
      this.previewLoading = true;
      this.previewProgress = null;
      this.filesService.getBlobFileWithProgress(fileName).subscribe({
        next: (event: any) => {
          // HttpEventType: Sent=0, UploadProgress=1, ResponseHeader=2, DownloadProgress=3, Response=4, User=5
          if (event?.type === 3) {
            if (event.total) {
              this.previewProgress = Math.round((event.loaded / event.total) * 100);
            } else {
              this.previewProgress = null; // indeterminate
            }
          } else if (event?.type === 4) {
            const resp = event;
            // Detectar tipo desde Content-Type o desde el nombre del archivo
            let contentType = resp.body?.type || resp.headers.get('Content-Type') || '';
            if (!contentType) {
              contentType = this.getFileTypeFromName(fileName);
            }
            const blob = new Blob([resp.body as any], { type: contentType });
            const name = fileName;
            this.uploadedFile = new File([blob], name, { type: contentType });
            this.previewUrl = URL.createObjectURL(blob);
            this.sanitizedPreviewUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.previewUrl);
            this.previewLoading = false;
            this.previewProgress = 100;
            if (openModal) {
              this.showPreviewModal = true;
            }
            this.detectChanges();
          }
        },
        error: (err) => {
          console.error('Error fetching preview from backend', err);
          this.previewLoading = false;
          this.previewProgress = null;
          this.errorMessage = 'No fue posible cargar la vista previa.';
          setTimeout(() => this.errorMessage = null, 5000);
        }
      });
    } catch (e) {
      console.error('Error in fetchPreviewFromBackend', e);
    }
  }

  private getFileTypeFromName(fileName: string): string {
    const extension = fileName.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'pdf':
        return 'application/pdf';
      case 'jpg':
      case 'jpeg':
        return 'image/jpeg';
      case 'png':
        return 'image/png';
      default:
        return 'application/octet-stream';
    }
  }

  onBrowseClick(input: HTMLInputElement) {
		if (this.readonly || !this.canAddMore) {
			return;
		}
		input.click();
	}

  onFileChange(event: Event) {
    if (!this.canAddMore) {
      this.errorMessage = 'No puedes subir más archivos. Máximo 1 archivo en total.';
      setTimeout(() => this.errorMessage = null, 5000);
      this.detectChanges();
      return;
    }
    const input = event.target as HTMLInputElement;
    const file = input.files && input.files[0] ? input.files[0] : null;
    if (file) {
      this.compressDocument(this.documentType?.type, file);
    }
    // Reset input to allow reselecting same file
    if (input) input.value = '';
  }

  onDragOver(event: DragEvent) {
		 if (this.readonly) return;
    if (!this.canAddMore) {
      event.preventDefault();
      return;
    }
    event.preventDefault();
    this.isDragging = true;
  }

  onDragLeave(event: DragEvent) {
		 if (this.readonly || !this.canAddMore) {
      return;
    }
    event.preventDefault();
    this.isDragging = false;
  }

  onDrop(event: DragEvent) {
    if (this.readonly || !this.canAddMore) {
      event.preventDefault();
      this.isDragging = false;
      this.errorMessage = 'No puedes subir más archivos. Máximo 1 archivo en total.';
      setTimeout(() => this.errorMessage = null, 5000);
      this.detectChanges();
      return;
    }
    event.preventDefault();
    this.isDragging = false;
    const file = event.dataTransfer && event.dataTransfer.files[0] ? event.dataTransfer.files[0] : null;
    if (file) {
      this.compressDocument(this.documentType?.type, file);
    }
  }

  clear() {
    this.fileName = undefined;
    this.success = undefined;
    this.errorMessage = null;
    this.deleting = false;
    if (this.previewUrl) {
      URL.revokeObjectURL(this.previewUrl);
      this.previewUrl = null;
    }
    this.sanitizedPreviewUrl = null;
    this.uploadedFile = null;
    this.fileSelected.emit(null);
  }

  removeFile(): void {
    // Prevenir eliminaciones mientras hay una en proceso
		if (this.readonly) return;
    if (this.deleting) {
      return;
    }

    if (!this.fileName) {
      return;
    }

    const fileName = this.fileName;

    // Mostrar modal de confirmación antes de eliminar
    Swal.fire({
      title: '¿Eliminar archivo?',
      text: `¿Estás seguro de que deseas eliminar el archivo "${fileName}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar',
      buttonsStyling: false,
      customClass: {
        confirmButton: 'btn btn-primary swal-confirm-button',
        cancelButton: 'btn btn-secondary swal-cancel-button'
      }
    }).then((result) => {
      if (result.isConfirmed) {
        this.executeFileDeletion(fileName);
      }
    });
  }

  private executeFileDeletion(fileName: string): void {
    // Prevenir eliminaciones mientras hay una en proceso
    if (this.deleting) {
      return;
    }

    // Activar estado de eliminación
    this.deleting = true;
    this.detectChanges();

    // Si el archivo tiene previewUrl, revocarlo para liberar memoria
    if (this.previewUrl) {
      URL.revokeObjectURL(this.previewUrl);
      this.previewUrl = null;
    }

    // Buscar el id en blobFiles basado en el nombre del archivo extraído de la URL
    let blobFileId: number | null = null;
    if (this.blobFiles && this.blobFiles.length > 0) {
      const blobFile = this.blobFiles.find((blob: any) => {
				if (!blob.url) return false;
				const blobFileName = this.extractFileNameFromUrl(blob.url);
				return blobFileName === fileName &&
					(this.documentType?.documentId == null || blob.pendencyId === this.documentType?.documentId);
			});

      if (blobFile && blobFile.id) {
        blobFileId = blobFile.id;
      }
    }

    // Eliminar de forma transaccional (blob storage y base de datos)
    this.filesService.deleteBlobFileTransactional(fileName, blobFileId).subscribe({
      next: (success) => {
        if (success) {
          // Guardar el nombre del archivo antes de limpiar
          const deletedFileName = fileName;

          // Limpiar el estado del componente solo si ambas operaciones fueron exitosas
          // PERO mantener deleting en true para el toast
          this.fileName = undefined;
          this.success = undefined;
          this.errorMessage = null;
          if (this.previewUrl) {
            URL.revokeObjectURL(this.previewUrl);
            this.previewUrl = null;
          }
          this.sanitizedPreviewUrl = null;
          this.uploadedFile = null;
          this.fileSelected.emit(null);

          // Emitir evento para notificar al componente padre que se eliminó un archivo
          this.fileDeleted.emit({ fileName: deletedFileName, blobFileId: blobFileId });

          this.detectChanges();

          // Mantener deleting en true mientras el toast está visible
          // (ya está en true desde el inicio de la eliminación)

          // Mostrar mensaje toast de éxito
          const toast = Swal.fire({
            title: 'Archivo eliminado',
            text: `El archivo "${deletedFileName}" ha sido eliminado correctamente.`,
            icon: 'success',
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 2000,
            timerProgressBar: true,
            didOpen: (toast) => {
              toast.addEventListener('mouseenter', Swal.stopTimer);
              toast.addEventListener('mouseleave', Swal.resumeTimer);
            }
          });

          // Forzar detección de cambios para asegurar que los botones se deshabiliten visualmente
          this.detectChanges();

          // Esperar a que el toast se cierre antes de permitir nuevas eliminaciones
          toast.then(() => {
            this.deleting = false;
            this.detectChanges();
          });
        } else {
          // Si falló pero no hay error, resetear estado
          this.deleting = false;
          this.detectChanges();
        }
      },
      error: (err) => {
        this.deleting = false;
        console.error('Error al eliminar archivo:', err);
        if (err.dbDeleted) {
          // El registro de BD fue eliminado pero falló la eliminación del blob
          this.errorMessage = 'El registro fue eliminado de la base de datos, pero hubo un error al eliminar el archivo del almacenamiento.';
        } else {
          // Falló la eliminación de BD, no se eliminó nada
          this.errorMessage = 'No fue posible eliminar el archivo.';
        }
        setTimeout(() => this.errorMessage = null, 5000);
        this.detectChanges();
      }
    });
  }

  private generatePreview(file: File): void {
    // Limpiar vista previa anterior
    if (this.previewUrl) {
      URL.revokeObjectURL(this.previewUrl);
    }

    // Asegurar que el tipo MIME esté correctamente establecido
    let fileType = file.type;
    if (!fileType || fileType === 'application/octet-stream') {
      fileType = this.getFileTypeFromName(file.name);
    }

    // Crear un nuevo File con el tipo correcto si es necesario
    let fileToPreview = file;
    if (fileType !== file.type) {
      fileToPreview = new File([file], file.name, { type: fileType });
    }

    if (fileType.startsWith('image/') || fileType === 'application/pdf') {
      this.previewUrl = URL.createObjectURL(fileToPreview);
      this.sanitizedPreviewUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.previewUrl);
    } else {
      this.previewUrl = null;
      this.sanitizedPreviewUrl = null;
    }
    this.uploadedFile = fileToPreview;
    this.detectChanges();
  }

  get canAddMore(): boolean {
    return !(this.fileName && this.success);
  }

  // Métodos de compresión
  public compressDocument(documentType: any, file: any): void {
    this.fileName = undefined;
    this.success = undefined;
    this.isPending = true;
    this.errorMessage = null;

    if (file.files) {
      file = file.files[0];
    }

    // Generar vista previa
    this.generatePreview(file);

    // Validación de formato para PJ
    if (this.clientType === 'PJ' && !file.type.includes('pdf') && !file.type.includes('jpg') && !file.type.includes('jpeg') && !file.type.includes('png') && !this.acceptAllFiles) {
      Swal.fire({
        title: '¡Atención!',
        text: `Sólo se aceptan archivos en formato PDF o Imagens (.JPG, .JPEG, .PNG).`,
        icon: 'warning',
        confirmButtonText: 'Ok',
        buttonsStyling: false,
        customClass: {
          confirmButton: 'btn btn-primary',
        },
      });
      this.isPending = false;
      return;
    }

    // Validación de tamaño
    if (file.size > this.maxSizeInBytes) {
      const sizeMB = (file.size / (1024 * 1024)).toFixed(2);
      this.errorMessage = `El archivo es muy grande (${sizeMB}MB). El tamaño máximo permitido es 60MB.`;
      this.isPending = false;
      setTimeout(() => this.errorMessage = null, 5000);
      return;
    }

    if (file.type.includes('pdf')) {
      this.compressIfPdf(documentType, file);
    } else {
      this.compressIfImage(documentType, file);
    }
  }

  private compressIfImage(documentType: any, file: any): void {
    const rawCompressedFile: BehaviorSubject<any> = new BehaviorSubject<any>(null);
    const compressingError: BehaviorSubject<any> = new BehaviorSubject<any>(null);

    rawCompressedFile.subscribe({
      next: rawCompressedFile => {
        if (rawCompressedFile) {
          const compressedFile = new File([rawCompressedFile], rawCompressedFile.name, {
            type: rawCompressedFile.type,
          });
          this.uploadDocument(documentType, compressedFile);
        }
      },
      error: (err: any) => {
        this.success = false;
        this.isPending = false;
        this.error = err;
        this.detectChanges();
      },
    });

    compressingError.subscribe({
      next: (errorMessage: any) => {
        if (errorMessage) {
          this.success = false;
          this.isPending = false;
          this.error = errorMessage;
          this.detectChanges();
        }
      },
    });

    new Compressor(file, {
      quality: 0.6,
      convertTypes: ['image/png', 'image/webp', 'image/tiff'],
      convertSize: 0,
      success(result) {
        rawCompressedFile.next(result);
      },
      error(err) {
        compressingError.next(err.message);
      },
    });
  }

  private compressIfPdf(documentType: any, pdfFile: any): void {
    let filePdfBytes: any;

    pdfFile.arrayBuffer().then(async (result: any) => {
      filePdfBytes = result;
      const formattedFilePdf = new File([filePdfBytes], pdfFile.name, { type: pdfFile.type });
      //const newPdf = await PDFDocument.load(filePdfBytes);
      this.uploadDocument(documentType, formattedFilePdf);
    });
  }

  private uploadDocument(documentType: any, compressedFile: any): void {
    if (this.checklist) {
      this.selectedFile.emit(compressedFile);
      setTimeout(() => {
        this.success = true;
        this.isPending = false;
        this.closeModal();
      }, 3000);
      return;
    } else {
      this.filesService.uploadDocument(documentType, compressedFile, this.documentType?.documentId, this.userId).subscribe({
        next: (response) => {
          this.success = true;
          this.isPending = false;
          this.fileName = response.data.fileName;
          this.closeModal();
          this.successUpload.emit(true);

          if (this.isPendence) {
            this.selectedFile.emit(compressedFile);
          }

          // Emitir evento para notificar al componente padre que se subió un archivo
          this.fileUploaded.emit({
            fileName: response.data.fileName,
            uri: response.data.uri,
            type: documentType?.type || documentType,
						documentId: this.documentType?.documentId
          });

          this.detectChanges();
        },
        error: (err: any) => {
          console.error('❌ [SingleUploader] Error en uploadDocument', err);
          this.success = false;
          this.isPending = false;
          this.error = err;
          this.detectChanges();
        },
      });
    }
  }

  private detectChanges(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.ref.detectChanges();
    }
  }

  // Métodos de webcam
  private handleWebcamSnapshotSize(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.webcamWidth = 600;
      this.webcamHeight = 500;

      if (window.innerWidth <= 650) {
        this.webcamWidth = 500;
      }

      if (window.innerWidth <= 550) {
        this.webcamWidth = 400;
      }

      if (window.innerWidth <= 450) {
        this.webcamWidth = 300;
        this.webcamHeight = 400;
      }

      if (window.innerWidth <= 350) {
        this.webcamWidth = 250;
      }
    }
  }

  private dataURItoBlob(dataURI: any): Blob | undefined {
    if (isPlatformBrowser(this.platformId)) {
      const byteString = window.atob(dataURI);
      const arrayBuffer = new ArrayBuffer(byteString.length);
      const int8Array = new Uint8Array(arrayBuffer);
      for (let i = 0; i < byteString.length; i++) {
        int8Array[i] = byteString.charCodeAt(i);
      }
      const blob = new Blob([int8Array], { type: 'image/jpeg' });
      return blob;
    }
    return;
  }

  public triggerSnapshot(): void {
    this.trigger.next(void 0);
  }

  public handleImage(documentType: any, webcamImage: WebcamImage): void {
    if (!this.canAddMore) {
      this.errorMessage = 'Ya tienes un archivo cargado. Máximo 1 archivo.';
      setTimeout(() => this.errorMessage = null, 5000);
      return;
    }

    this.webcamImage = webcamImage;
    this.capturedImageBase64 = webcamImage!.imageAsBase64;

    const formattedImageBlob = this.dataURItoBlob(this.capturedImageBase64);
    if (!formattedImageBlob) {
      this.errorMessage = 'Error al procesar la imagen de la cámara.';
      setTimeout(() => this.errorMessage = null, 5000);
      return;
    }
    // @ts-ignore
    const formattedImageFile = new File([formattedImageBlob], 'my-photo.jpg', { type: formattedImageBlob.type });

    this.compressDocument(documentType, formattedImageFile);
  }

  public get triggerObservable(): Observable<any> {
    return this.trigger.asObservable();
  }

  public closeModal(): void {
    this.showSnapshotModal = false;
  }

  public openPreviewModal(): void {
    if (this.previewUrl) {
      this.showPreviewModal = true;
      this.detectChanges();
      return;
    }

    // Si no hay previewUrl (archivo precargado), intentar obtenerlo del backend
    if (!this.previewUrl && this.userId && this.documentType?.type != null) {
      this.fetchPreviewFromBackend(true);
      return;
    }
  }

  public closePreviewModal(): void {
    this.showPreviewModal = false;
    this.detectChanges();
  }

  @HostListener('window:resize')
  public onResize(): void {
    this.isMobile = this.userAgent.verifyMobile();
    this.handleWebcamSnapshotSize();
  }
}


